import m from 'mithril';
import { Store } from '../store';
import { Button } from '../components/Buttons';

export const CartPage: m.Component = {
    view() {
        const cartItems = Store.state.cart;
        
        // Compute subtotal dynamically
        const subtotal = cartItems.reduce((acc: number, item: any) => acc + (Number(item.price) * (item.quantity || 1)), 0);
        const shipping = cartItems.length > 0 ? 15.00 : 0.00;
        const total = subtotal + shipping;

        const updateQuantity = (index: number, delta: number) => {
            if (!cartItems[index].quantity) cartItems[index].quantity = 1;
            const newQ = cartItems[index].quantity + delta;
            const maxStock = cartItems[index].product?.stock ?? 99;
            if (newQ > maxStock) {
                Store.addToast(`Only ${maxStock} items available in stock`, 'error');
                return;
            }
            cartItems[index].quantity = newQ;
            if (cartItems[index].quantity <= 0) {
                cartItems.splice(index, 1);
            }
            m.redraw();
        };

        const removeItem = (index: number) => {
            cartItems.splice(index, 1);
            m.redraw();
        };

        return m('div', { class: 'container mx-auto px-6 animate-fade-in-up py-8 transition-colors duration-500 text-[var(--color-text-base)] bg-[var(--color-bg-base)] min-h-[100dvh]' }, [
            m('div', { class: 'flex items-center mb-6 gap-2 cursor-pointer opacity-70 hover:text-[var(--color-primary)] transition-colors', onclick: () => m.route.set('/') }, [
                m('iconify-icon', { icon: 'lucide:arrow-left' }),
                m('span', { class: 'font-semibold' }, 'Continue Shopping')
            ]),
            
            m('div', { class: 'grid lg:grid-cols-3 gap-8' }, [
                // Cart Items Section
                m('div', { class: 'lg:col-span-2 flex flex-col gap-4' }, 
                    cartItems.length === 0 
                     ? m('div', { class: 'p-16 text-center bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-sm flex flex-col items-center justify-center animate-fade-in-up' }, [
                         m('div', { class: 'w-32 h-32 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] opacity-10 flex items-center justify-center mb-6' }, [
                             m('iconify-icon', { icon: 'lucide:shopping-bag', class: 'text-6xl text-[var(--color-primary)]' })
                         ]),
                         m('h2', { class: 'text-2xl font-black mb-2' }, 'Your cart is empty'),
                         m('p', { class: 'opacity-70 font-medium mb-8 max-w-sm' }, 'Looks like you haven\'t added any items to your cart yet. Explore our top products!'),
                         m(Button, { variant: 'primary', size: 'lg', onclick: () => m.route.set('/') }, 'Start Shopping')
                     ])
                     : cartItems.map((item: any, idx: number) => 
                        m('div', { class: 'card flex flex-col sm:flex-row gap-4 items-center bg-[var(--color-surface)] p-4 rounded-lg shadow-sm border border-[var(--color-border)] transition-colors' }, [
                            m('img', { src: item.images?.[0] || 'https://via.placeholder.com/200', loading: 'lazy', class: 'w-24 h-24 object-cover rounded-lg' }),
                            m('div', { class: 'flex-1 flex flex-col gap-1' }, [
                                m('h3', { class: 'font-bold text-lg' }, item.name),
                                m('span', { class: 'text-[var(--color-primary)] font-bold' }, `$${Number(item.price).toFixed(2)}`)
                            ]),
                            m('div', { class: 'flex items-center gap-4' }, [
                                m('div', { class: 'flex items-center border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-bg-base)]' }, [
                                    m(Button, { variant: 'ghost', size: 'icon', class: 'text-[var(--color-primary)]', onclick: () => updateQuantity(idx, -1) }, '-'),
                                    m('span', { class: 'px-3 font-semibold text-sm' }, item.quantity || 1),
                                    m(Button, { variant: 'ghost', size: 'icon', class: 'text-[var(--color-primary)]', onclick: () => updateQuantity(idx, 1) }, '+'),
                                ]),
                                m(Button, { variant: 'ghost', size: 'icon', class: 'text-red-500 hover:text-red-600 hover:bg-red-500/10', onclick: () => removeItem(idx) }, [
                                    m('iconify-icon', { icon: 'lucide:trash-2', class: 'text-xl' })
                                ])
                            ])
                        ])
                    )
                ),
                // Order Summary Section
                m('div', { class: 'lg:col-span-1' }, [
                    m('div', { class: 'bg-[var(--color-surface)] p-8 rounded-lg shadow-sm border border-[var(--color-border)] sticky top-24 transition-colors' }, [
                        m('h2', { class: 'text-xl font-bold mb-6 border-b border-[var(--color-border)] pb-4' }, 'Order Summary'),
                        m('div', { class: 'flex justify-between mb-4 opacity-70' }, [
                            m('span', 'Subtotal'),
                            m('span', { class: 'font-semibold text-[var(--color-primary)]' }, `$${subtotal.toFixed(2)}`)
                        ]),
                        m('div', { class: 'flex justify-between mb-4 opacity-70' }, [
                            m('span', 'Estimated Shipping'),
                            m('span', { class: 'font-semibold text-[var(--color-primary)]' }, `$${shipping.toFixed(2)}`)
                        ]),
                        m('div', { class: 'border-t border-[var(--color-border)] pt-4 mt-4 flex justify-between font-bold text-xl mb-8' }, [
                            m('span', 'Total'),
                            m('span', { class: 'text-[var(--color-primary)]' }, `$${total.toFixed(2)}`)
                        ]),
                        m(Button, { 
                            variant: 'primary',
                            size: 'lg',
                            class: 'w-full animate-pulse-slow block text-center', 
                            disabled: cartItems.length === 0,
                            onclick: async () => {
                                if (!Store.state.user) {
                                    // Allow guest to proceed to checkout where they will be auto-registered
                                    m.route.set('/checkout');
                                    return;
                                }
                                try {
                                    // Map local cart array to expected API format
                                    const syncPayload = cartItems.map((item: any) => ({
                                        productId: item.product?.id || item.id,
                                        quantity: item.quantity || 1
                                    }));
                                    
                                    await import('../services/api').then(module => 
                                        module.ApiService.syncCart(syncPayload)
                                    );
                                    m.route.set('/checkout');
                                } catch (error) {
                                    Store.addToast('Failed to sync cart with server.', 'error');
                                    console.error('Checkout Sync Error:', error);
                                }
                            }
                        }, 'Proceed to Checkout')
                    ])
                ])
            ])
        ]);
    }
};
